import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { useSidebar } from '../contexts/SidebarContext';
import { isAdminUser } from '../utils/admin';
import { 
  FaHome, 
  FaBicycle, 
  FaChevronLeft, 
  FaChevronRight,
  FaBars,
  FaUser,
  FaCog,
  FaSignOutAlt,
  FaTools,
  FaBolt,
  FaShoppingCart,
  FaQuestionCircle,
  FaCalendarAlt,
  FaChartArea,
  FaBookOpen,
  FaDownload,
  FaDotCircle,
} from 'react-icons/fa';
import './Sidebar.css';

import packageJson from '../../package.json';

const NARROW_BREAKPOINT = 768;
const APP_VERSION = packageJson.version || '0.0.0';

const Sidebar = () => {
  const { isCollapsed, setIsCollapsed, toggleSidebar } = useSidebar();
  const [userData, setUserData] = useState(null);
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth <= NARROW_BREAKPOINT);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth <= NARROW_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isNarrow) setIsCollapsed(true);
  }, [isNarrow, setIsCollapsed]);

  useEffect(() => {
    if (!isNarrow || isCollapsed) return;
    const onEscape = (e) => {
      if (e.key === 'Escape') toggleSidebar();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [isNarrow, isCollapsed, toggleSidebar]);

  useEffect(() => {
    if (!user) {
      setUserData(null);
      return;
    }
    const docRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        setUserData(snapshot.exists() ? snapshot.data() : null);
      },
      (err) => {
        if (err?.code === 'permission-denied') {
          console.warn(
            '[Firestore] users/' +
              user.uid +
              ' read denied. Merge config/firestore-users.rules.fragment (or full rules) in Firebase → Firestore → Rules.',
          );
        } else {
          console.warn('[Firestore] users snapshot error:', err);
        }
        setUserData(null);
      },
    );
    return () => unsubscribe();
  }, [user]);

  const userCredits = userData?.credits ?? 0;
  const isAdmin = isAdminUser(user, userData);

  const baseMenuItems = [
    { path: '/home', icon: FaHome, label: 'Home' },
    { path: '/my-rides', icon: FaBicycle, label: 'My Runs' },
    { path: '/live', icon: FaDotCircle, label: 'Live' },
    { path: '/calendar', icon: FaCalendarAlt, label: 'Calendar' },
    { path: '/help', icon: FaQuestionCircle, label: 'Help' },
  ];

  const adminMenuItems = isAdmin
    ? [
        { path: '/admin', icon: FaTools, label: 'Admin Panel (admin)' },
        {
          key: 'packing-analyzer',
          icon: FaChartArea,
          label: 'Open packing analyzer (admin)',
          externalUrl: `${process.env.PUBLIC_URL || ''}/jack-benchmark.html`,
        },
      ]
    : [];

  const menuItems = [
    ...baseMenuItems,
    ...adminMenuItems,
    { key: 'tuning-guide', path: '/tuning-guide', icon: FaBookOpen, label: 'Tuning guide' },
  ];

  const handleNavigation = (path) => {
    navigate(path);
    if (isNarrow) toggleSidebar();
  };

  const isNavItemActive = (item) => item.path && location.pathname === item.path;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/landing');
      if (isNarrow) toggleSidebar();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isNarrow && !isCollapsed && (
        <div
          className="sidebar-backdrop"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
      <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isNarrow && !isCollapsed ? 'sidebar-overlay' : ''}`}>
        {/* Logo Section */}
        <div className="sidebar-logo">
          <div className="logo-container">
            <img
              src={isCollapsed ? '/logo_shokiq.svg' : '/logo_shokiq_horizontal.svg'}
              alt="Logo"
              className="logo-image"
            />
            {!isCollapsed && <span className="beta-label">v{APP_VERSION}</span>}
          </div>
        </div>

        {/* Toggle Button (hamburger when narrow + collapsed) */}
        <button 
          className="sidebar-toggle"
          onClick={toggleSidebar}
          aria-label={isCollapsed ? (isNarrow ? 'Open menu' : 'Expand sidebar') : 'Collapse sidebar'}
        >
          {isNarrow && isCollapsed ? <FaBars /> : isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

      {/* Navigation Items */}
      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <button
            key={item.key || item.path}
            type="button"
            className={`sidebar-item ${item.path && isNavItemActive(item) ? 'active' : ''}`}
            onClick={() => {
              if (item.externalUrl) {
                window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                if (isNarrow) toggleSidebar();
              } else {
                handleNavigation(item.path);
              }
            }}
            title={isCollapsed ? item.label : ''}
          >
            <item.icon className="sidebar-icon" />
            {!isCollapsed && <span className="sidebar-label">{item.label}</span>}
          </button>
        ))}
      </nav>

      {/* User Section at Bottom */}
      <div className="sidebar-user">
        <button
          type="button"
          className={`sidebar-item ${isActive('/settings') ? 'active' : ''}`}
          onClick={() => handleNavigation('/settings')}
          title={isCollapsed ? 'Settings' : ''}
        >
          <FaCog className="sidebar-icon" />
          {!isCollapsed && <span className="sidebar-label">Settings</span>}
        </button>

        <button
          className="sidebar-item user-profile"
          onClick={() => handleNavigation('/profile')}
          title={isCollapsed ? userData?.username || user?.displayName || user?.email : ''}
        >
          <FaUser className="sidebar-icon" />
          {!isCollapsed && (
            <div className="user-info">
              <span className="user-name">
                {userData?.username || user?.displayName || user?.email?.split('@')[0] || 'User'}
              </span>
            </div>
          )}
        </button>

        {/* Credits Section - Under Profile */}
        {user && (
          <div className="sidebar-credits-bottom">
            <div 
              className="credits-display"
              onClick={() => handleNavigation('/buy-credits')}
              title="Buy Credits"
            >
              <FaBolt className="credits-icon" />
              {!isCollapsed && (
                <>
                  <span className="credits-amount">{userCredits}</span>
                  <span className="credits-label">Credits</span>
                </>
              )}
            </div>
            {!isCollapsed && (
              <button 
                className="buy-credits-btn"
                onClick={() => handleNavigation('/buy-credits')}
              >
                <FaShoppingCart /> Buy Credits
              </button>
            )}
          </div>
        )}
        
        <button
          className="sidebar-item logout"
          onClick={handleLogout}
          title="Logout"
        >
          <FaSignOutAlt className="sidebar-icon" />
          {!isCollapsed && <span className="sidebar-label">Logout</span>}
        </button>
        <button
          type="button"
          className="sidebar-item install-app-trigger"
          onClick={() => setShowInstallHelp(true)}
          title={isCollapsed ? 'Install App' : ''}
        >
          <FaDownload className="sidebar-icon" />
          {!isCollapsed && <span className="sidebar-label">Install App</span>}
        </button>
      </div>
    </div>
    {showInstallHelp ? (
      <div
        className="install-app-modal-overlay"
        onClick={() => setShowInstallHelp(false)}
        role="presentation"
      >
        <div
          className="install-app-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="install-app-modal-title"
        >
          <div className="install-app-modal-header">
            <img src="/logo_circle.png" alt="ShokIQ icon" className="install-app-modal-logo" />
            <div>
              <h3 id="install-app-modal-title">Install ShokIQ on your phone</h3>
              <p>Add this app to your home screen for quick access and full-screen mode.</p>
            </div>
          </div>
          <div className="install-app-steps">
            <div>
              <strong>iPhone (Safari)</strong>
              <ol>
                <li>Open this app in Safari.</li>
                <li>Tap Share, then tap Add to Home Screen.</li>
                <li>Confirm the name, then tap Add.</li>
              </ol>
            </div>
            <div>
              <strong>Android (Chrome)</strong>
              <ol>
                <li>Open this app in Chrome.</li>
                <li>Tap the menu, then Add to Home screen or Install app.</li>
                <li>Confirm to add it to your home screen.</li>
              </ol>
            </div>
          </div>
          <div className="install-app-modal-actions">
            <button type="button" className="browse-button" onClick={() => setShowInstallHelp(false)}>
              Close
            </button>
          </div>
        </div>
      </div>
    ) : null}
    </>
  );
};

export default Sidebar;

